'use client'

import { useEffect, useRef, useState } from 'react'

import { IconPlay } from '@/app/components/atoms/Icons'
import { Poster } from '@/app/components/atoms/Poster'
import { cx } from '@/app/lib/utils'

import styles from './YoutubeVideoThumb.module.scss'
import type { YoutubeVideoThumbProps } from './YoutubeVideoThumb.types'

/**
 * Miniatura de un vídeo de YouTube con icono de reproducción superpuesto,
 * que se sustituye por un `<iframe>` reproduciéndose solo al pulsarla, sin
 * autoplay ni vista ampliada (lightbox). Promovido desde
 * `modules/Multimedia/MultimediaVideoThumb.tsx` (research.md §3 de
 * `specs/044-hero-galeria`) — mismo patrón visual, ahora compartido entre el
 * módulo Multimedia y `HeroGaleria`. Vista previa vía la miniatura pública
 * de YouTube (`img.youtube.com`, ya whitelisteada en `next.config.ts`).
 */
export function YoutubeVideoThumb({ videoId, title, className }: YoutubeVideoThumbProps) {
  const [playing, setPlaying] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // El botón se desmonta al activar `playing` — sin este traspaso explícito,
  // el foco cae a `<body>` y un usuario de teclado/lector de pantalla pierde
  // su posición en la página (auditoría de accesibilidad, specs/044-hero-galeria).
  useEffect(() => {
    if (playing) iframeRef.current?.focus()
  }, [playing])

  if (playing) {
    return (
      <iframe
        ref={iframeRef}
        className={cx(styles.iframe, className)}
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    )
  }

  return (
    <button
      type="button"
      className={cx(styles.playThumb, className)}
      aria-label={`Reproducir: ${title}`}
      onClick={() => setPlaying(true)}
    >
      <Poster
        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
        alt=""
        className={styles.media}
      />
      <span className={styles.playIcon} aria-hidden="true">
        <IconPlay size={24} />
      </span>
    </button>
  )
}
